/**
 * @type {import("@pattern-library/base/dist/js/hooks/useFormValidation").ValidationHandler}
 */
export function focusFirstInvalidInput(result) {
    if (!result.isValid) {
        const firstError = result.errors[0];

        const firstInvalidInput = result.formElement.elements[firstError.name];
        firstInvalidInput?.focus();
    }
}

/**
 * @typedef {object} AriaLiveMessageOptions
 * @prop {string | HTMLElement | ((result: import("@pattern-library/base/dist/js/hooks/useFormValidation").ValidationResult) => HTMLElement)} target
 * @prop {string | ((result: import("@pattern-library/base/dist/js/hooks/useFormValidation").ValidationResult) => string)} [message]
 */

/**
 * @param {AriaLiveMessageOptions} [options]
 * @returns {import("@pattern-library/base/dist/js/hooks/useFormValidation").ValidationHandler}
 */
export function updateAriaLiveMessage({ target, message = defaultAriaLiveMessage } = {}) {
    return result => {
        const liveRegion = findLiveRegion(target, result);

        if (!liveRegion) {
            return;
        }

        if (result.isValid) {
            liveRegion.textContent = '';
        } else {
            const msg = typeof message === 'function' ? message(result) : message;
            liveRegion.textContent = msg;
        }
    };
}

/**
 * @param {import("@pattern-library/base/dist/js/hooks/useFormValidation").ValidationResult} result
 * @returns {string}
 */
function defaultAriaLiveMessage(result) {
    const count = result.errors.length;
    const fields = count === 1 ? 'field' : 'fields';
    const are = count === 1 ? 'is' : 'are';
    return `Failed to submit because ${count} ${fields} ${are} invalid.`;
}

/**
 * @param {AriaLiveMessageOptions['target']} target
 * @param {import("@pattern-library/base/dist/js/hooks/useFormValidation").ValidationResult} result
 * @returns {HTMLElement | null}
 */
function findLiveRegion(target, result) {
    if (target instanceof HTMLElement) {
        return target;
    }

    if (typeof target === 'function') {
        return target(result);
    }

    return document.getElementById(target);
}

/**
 * @param {...import("@pattern-library/base/dist/js/hooks/useFormValidation").ValidationHandler} handlers
 * @returns {import("@pattern-library/base/dist/js/hooks/useFormValidation").ValidationHandler}
 */
export function combine(...handlers) {
    return function (result) {
        handlers.forEach(handle => handle(result));
    };
}
